/**
 * Aba "Checklist - Atendimento" da ficha: 13 perguntas em texto que a consultora preenche.
 *
 * Porte de `portal/admin.html:2631-2636` (a aba), `5112-5122` (montar o formulário) e `5339-5363`
 * (ler e salvar). Linha do inventário: 198.
 *
 * Território da frente A2. A moldura (`../index.tsx`) importa esta aba pelo nome `AbaAtendimento`.
 */

// Estado e retorno estável do React.
import { useCallback, useState } from 'react';
// As peças do Chassi para texto longo e botão.
import { Botao, CampoTexto } from '@complexity/ui';
// A leitura e a gravação da aba, no escopo da ficha.
import { lerChecklistAtendimento, salvarChecklistAtendimento } from '@studiolu/dados/areas/ficha';
// A fonte de dados.
import { usePainel } from '../../../contexto.js';
// O status que some sozinho (A5).
import { LinhaDeStatus, useStatusTemporario } from '../../../comum/status-temporario.js';
// As 13 perguntas, da cópia única.
import { PERGUNTAS_ATENDIMENTO } from '../constantes.js';
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// O contrato de carga: a moldura lê esta aba antes de mostrá-la e relê a cada toque no botão dela.
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// Os apoios comuns das abas (A3).
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';

// As colunas, na ordem das perguntas.
const CAMPOS = Object.keys(PERGUNTAS_ATENDIMENTO);

export function AbaAtendimento({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nem grava nada.
  if (!ficha) return <AbaSemFicha aba="Checklist - Atendimento" />;
  return <ChecklistAtendimento ficha={ficha} />;
}

function ChecklistAtendimento({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const escopo = useEscopoEstavel(ficha);
  const { somenteLeitura } = useRecortePorPapel();
  // As respostas por coluna, vazias até a leitura voltar.
  const [valores, setValores] = useState<Readonly<Record<string, string>>>({});
  // O salvar só destrava depois da leitura (`admin.html:5964` e `5348`).
  const [carregado, setCarregado] = useState(false);
  const [status, mostrar, fixar] = useStatusTemporario();

  // A leitura (`carregarChecklistAtendimento`, `admin.html:5339-5349`): limpa o status e preenche.
  const carregar = useCallback(async () => {
    fixar('');
    const r = await lerChecklistAtendimento(dados, escopo);
    if (!r.ok) console.error('Erro ao carregar checklist de atendimento:', r.erro);
    const linha = (r.ok ? r.dados : null) as Readonly<Record<string, unknown>> | null;
    const lidos: Record<string, string> = {};
    // `data?.[campo] || ''`: nulo e vazio viram vazio.
    for (const campo of CAMPOS) lidos[campo] = (linha?.[campo] as string | null | undefined) || '';
    setValores(lidos);
    setCarregado(true);
  }, [dados, escopo, fixar]);

  /*
   * A leitura pelo contrato da moldura (`comum-ficha/carregador.ts`): antes de a aba aparecer e a
   * cada toque no botão dela (`admin.html:5086-5091`). Aqui o carregador LIMPA a linha de status
   * (`5340`), ao contrário do Questionário e da Personalidade, e por isso o "Salvo!" desta aba não
   * sobrevive à volta, também como no legado.
   */
  useCarregadorDaAba(carregar);

  /**
   * O salvar (`admin.html:5351-5363`). Diferente do Questionário Geral, este botão NÃO trava
   * enquanto grava: o legado não o desabilita, e fica igual.
   */
  const salvar = async () => {
    fixar('Salvando...');
    const registro: Record<string, unknown> = {};
    for (const campo of CAMPOS) registro[campo] = valores[campo] ?? '';
    const r = await salvarChecklistAtendimento(dados, escopo, registro);
    if (!r.ok) {
      fixar(`Erro ao salvar: ${r.erro.mensagem}`);
      return;
    }
    mostrar('Salvo!');
  };

  return (
    <div className="flex flex-col gap-cx-4">
      <h3 className="text-cx-lg font-semibold text-texto">Checklist de Atendimento</h3>
      {/* "Apenas Consulta": todo campo desabilitado (`admin.html:3393`). */}
      <fieldset disabled={somenteLeitura} className="m-0 flex min-w-0 flex-col gap-cx-4 border-0 p-0">
        {CAMPOS.map((campo) => (
          <CampoTexto
            key={campo}
            rotulo={PERGUNTAS_ATENDIMENTO[campo]!}
            linhas={3}
            value={valores[campo] ?? ''}
            onChange={(e) => {
              const valor = e.target.value;
              setValores((v) => ({ ...v, [campo]: valor }));
            }}
          />
        ))}
      </fieldset>
      {!somenteLeitura && (
        <Botao className="self-start" disabled={!carregado} onClick={() => void salvar()}>
          Salvar checklist de atendimento
        </Botao>
      )}
      <LinhaDeStatus texto={status} testid="status-atendimento" />
    </div>
  );
}
